import React from "react";

const TOTAL_SEATS = 435;
const MAJORITY = 218;

const ParliamentPlot = ({
  seatCounts,
  colors,
  partyOrder,
  rows = 14,
  dotRadius = 0.4,
  width = 220,
  height = 115,
  totalSeats = TOTAL_SEATS,
  majorityAt = MAJORITY,
}) => {
  if (!seatCounts || !colors) return null;

  // 1. Build seat sequence left→right by party order
  const orderedKeys = (partyOrder || Object.keys(seatCounts)).filter(
    (k) => (seatCounts[k] || 0) > 0
  );

  const seatsSeq = [];
  orderedKeys.forEach((k) => {
    for (let i = 0; i < seatCounts[k]; i++) seatsSeq.push(k);
  });

  const actualTotal = seatsSeq.length;
  if (!actualTotal) return null;

  // 2. Geometry
  const cx = width / 2;
  const cy = height - 6;
  const outerR = Math.min(width / 2 - 12, 100);
  const innerR = outerR * 0.35;

  const radii = [];
  for (let i = 0; i < rows; i++) {
    const r = outerR - i * ((outerR - innerR) / (rows - 1));
    if (r > 0) radii.push(r);
  }

  // 3. Fixed angular range — ALL rows share the same start/end angle
  //    so the edges form straight radial lines
  const startAngle = 0.1;
  const endAngle = Math.PI - 0.1;

  // 4. Allocate seats per row proportional to arc length
  const arcs = radii.map((r) => r * (endAngle - startAngle));
  const totalArc = arcs.reduce((a, b) => a + b, 0);

  let remaining = actualTotal;
  const seatsPerRow = radii.map((_, i) => {
    if (i === radii.length - 1) return remaining;
    const n = Math.round((arcs[i] / totalArc) * actualTotal);
    remaining -= n;
    return Math.max(n, 0);
  });

  // 5. Create slots — first and last dot in every row sit at exactly
  //    startAngle and endAngle, giving perfectly aligned edges
  const allSlots = [];
  radii.forEach((r, row) => {
    const n = seatsPerRow[row];
    if (n <= 0) return;
    for (let j = 0; j < n; j++) {
      const angle =
        n === 1
          ? Math.PI / 2
          : startAngle + (j / (n - 1)) * (endAngle - startAngle);
      allSlots.push({
        x: cx + r * Math.cos(angle),
        y: cy - r * Math.sin(angle),
        angle,
        row,
      });
    }
  });

  // 6. Sort by angle (left→right sweep) for clean radial party boundary
  allSlots.sort((a, b) => a.angle - b.angle || a.row - b.row);

  // 7. Assign colors
  const filled = allSlots.map((s, i) => ({
    ...s,
    color: colors[seatsSeq[i] || ""] || "#ccc",
  }));


  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block", margin: "0 auto" }}
      aria-label={`Parliament chart showing ${actualTotal} seats`}
    >
      {filled.map((s, i) => (
        <circle key={i} cx={s.x} cy={s.y} r={dotRadius} fill={s.color} />
      ))}

      
    </svg>
  );
};

const SeatTable = ({
  seatCounts,
  labels,
  colors,
}) => {
  if (!seatCounts) return null;

  let entries = Object.entries(seatCounts).filter(([, n]) => n > 0);

  const isOther = ([k]) => /other|ind/i.test(k);
  const others = entries.filter(isOther);
  entries = entries.filter((e) => !isOther(e));
  entries.sort((a, b) => b[1] - a[1]);
  entries.push(...others);

  const maxSeats = Math.max(...entries.map(([, n]) => n), 1);

  return (
    <table
      style={{
        fontSize: 12,
        borderCollapse: "collapse",
        margin: "0 auto",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <tbody>
        {entries.map(([k, n]) => (
          <tr key={k}>
            <td style={{ padding: "3px 8px", whiteSpace: "nowrap" }}>
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  marginRight: 6,
                  backgroundColor: colors?.[k] || "#999",
                  verticalAlign: "middle",
                }}
              />
              {labels?.[k] || k}
            </td>
            <td
              style={{
                padding: "3px 8px",
                textAlign: "right",
                fontWeight: 600,
              }}
            >
              {n}
            </td>
            <td style={{ padding: "3px 8px", width: 60 }}>
              <div
                style={{
                  height: 8,
                  borderRadius: 4,
                  width: `${(n / maxSeats) * 100}%`,
                  backgroundColor: colors?.[k] || "#999",
                  opacity: 0.5,
                }}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export { ParliamentPlot, SeatTable, TOTAL_SEATS, MAJORITY };
export default ParliamentPlot;